import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * The Run Summary CSV contract, shared by this step's output and the backend
 * parser so a new status is added in one place. Format documented in
 * `docs/pricing/vendors/run-summary-csv.md`.
 */
export const RUN_SUMMARY_STATUSES = [
  "could_not_open",
  "download_failed",
  "no_price_lists",
] as const;

/** Matches `abb-run-summary.csv` and any other `<vendor>-run-summary.csv`. */
export const DEFAULT_RUN_SUMMARY_FILE_PATTERN = "run-summary\\.csv$";

export const runSummaryAccountSchema = z.object({
  accountNumber: z
    .string()
    .describe("The vendor's account number, exactly as written"),
  accountName: z.string(),
  status: z.enum(RUN_SUMMARY_STATUSES),
  files: z
    .number()
    .int()
    .nonnegative()
    .describe("Files downloaded for the account before it stopped"),
  error: z.string(),
});

export const runSummaryCountsSchema = z.object({
  could_not_open: z.number().int().nonnegative(),
  download_failed: z.number().int().nonnegative(),
  no_price_lists: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

/**
 * Email Run Summary
 *
 * Placed after a Vendor Portal Procedure's `web_browser` step. Finds the Run
 * Summary CSV among the browser step's downloads, parses it, saves it as this
 * step's output, and emails the user who started the run which Vendor
 * Accounts need action (Failed Accounts by reason, accounts with no Price
 * Lists), with the CSV attached. The CSV format is documented in
 * `docs/pricing/vendors/run-summary-csv.md`.
 *
 * It always emails, including when the browser step failed or wrote no
 * summary. A run with no starting user (system-triggered) sends nothing but
 * still saves its output, which Vendor Portals reads for its failed-runs icon.
 *
 * Example workflow:
 * ```
 * Action Input → Web Browser (procedure) → Email Run Summary
 * ```
 */
export const emailRunSummaryStep = defineStepConfig({
  type: "email_run_summary",
  name: "Email Run Summary",
  description:
    "Email the run's starter which Vendor Accounts failed or had no Price Lists, from the Run Summary CSV a Vendor Portal Procedure downloaded",
  category: "Integration",
  libraryModule: "Pricing",

  inputSchema: z.object({
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "The browser step's downloaded Documents (web_browser's documentIds); the Run Summary CSV is found among them by name.",
      ),
    // Required, not defaulted: an unwired success would report a failed run
    // as a successful one.
    success: z.boolean().describe("The browser step's success output"),
    error: z
      .string()
      .nullable()
      .optional()
      .describe("The browser step's error output, stated in the email"),
  }),

  outputSchema: z.object({
    summaryDocumentId: z
      .string()
      .nullable()
      .describe("The Run Summary CSV's Document, or null when none was found"),
    summaryError: z
      .string()
      .nullable()
      .describe(
        "Why the summary, or some of its lines, could not be read; null when it read cleanly",
      ),
    runSucceeded: z.boolean().describe("Whether the browser step succeeded"),
    runError: z.string().nullable(),
    counts: runSummaryCountsSchema.describe(
      "Accounts needing attention, by status",
    ),
    // Wrapped in an object so the socket is one JSON value, not an array
    // that would fan downstream steps out per account.
    accounts: z
      .object({ items: z.array(runSummaryAccountSchema) })
      .describe("Every account the summary lists, in file order"),
    emailSent: z.boolean(),
    recipient: z
      .string()
      .nullable()
      .describe("The starter's email, or null for a system-triggered run"),
    emailError: z
      .string()
      .nullable()
      .describe("Why the email could not be sent, when it could not"),
  }),

  configSchema: z.object({
    summaryFilePattern: z
      .string()
      .default(DEFAULT_RUN_SUMMARY_FILE_PATTERN)
      .describe(
        "Regex (case-insensitive) on the downloaded file name that identifies the Run Summary CSV",
      ),
  }),

  execution: {
    // The send carries an idempotency key tied to the step run, so a retry
    // (including one after the worker died mid-step) cannot email twice.
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },

  emits: [] as const,
});
